import { useCallback, useEffect, useState } from 'react';
import api from '../services/api.js';

const amount = (q, unit) => `${Number(q).toLocaleString('en-US', { maximumFractionDigits: 2 })}${unit ? ` ${unit}` : ''}`;
const when = (ts) =>
  ts ? new Date(ts).toLocaleString('en-US', { timeZone: 'Asia/Manila', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

/** On board: confirm deliveries sent to this tug, and record items used up. */
export default function TugInventory({ tug }) {
  const [pending, setPending] = useState([]);
  const [suggest, setSuggest] = useState({ items: [], storedIn: [] });
  const [drafts, setDrafts] = useState({}); // delivery id -> { qty, storedIn, note }
  const [used, setUsed] = useState({ item: '', qty: '', unit: '', note: '' });
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([api.getInventoryPending(tug.id), api.getInventorySuggestions(tug.id)]);
      setPending(p);
      setSuggest(s);
    } catch (e) {
      setMsg({ type: 'danger', text: e.message || 'Failed to load deliveries.' });
    }
  }, [tug.id]);
  useEffect(() => { load(); }, [load]);

  const fail = (err, fallback) => setMsg({ type: 'danger', text: err.details?.[0]?.message || err.message || fallback });
  const draft = (p) => drafts[p.id] || { qty: String(p.qty), storedIn: '', note: '' };
  const edit = (p, patch) => setDrafts((d) => ({ ...d, [p.id]: { ...draft(p), ...patch } }));

  const confirm = async (p) => {
    setMsg(null);
    const d = draft(p);
    try {
      const done = await api.receiveInventory(p.id, { receivedQty: d.qty === '' ? null : Number(d.qty), storedIn: d.storedIn.trim() || null, note: d.note.trim() || null });
      setMsg({
        type: done.discrepancy ? 'warning' : 'success',
        text: done.discrepancy ? `${done.item}: received ${amount(done.receivedQty, done.unit)} of ${amount(done.qty, done.unit)} sent. The office will see the difference.` : `${done.item} received.`,
      });
      await load();
    } catch (err) {
      fail(err, 'Failed to confirm receipt.');
    }
  };

  const recordUsed = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (!used.item.trim() || !used.qty) {
      setMsg({ type: 'danger', text: 'Item and quantity are required.' });
      return;
    }
    try {
      const done = await api.recordInventoryUsed({ item: used.item.trim(), qty: Number(used.qty), unit: used.unit.trim() || null, tugId: tug.id, note: used.note.trim() || null });
      setMsg({ type: 'success', text: `${done.item}: ${amount(done.qty, done.unit)} recorded as used up.` });
      setUsed({ item: '', qty: '', unit: '', note: '' });
      await load();
    } catch (err) {
      fail(err, 'Failed to record usage.');
    }
  };

  return (
    <div className="card border-0 shadow-sm mb-3" data-testid="tug-inventory">
      <div className="card-header bg-white pt-3 border-bottom-0">
        <h6 className="mb-0 fw-bold text-uppercase small text-secondary">Stores — {tug.name}</h6>
      </div>
      <div className="card-body">
        {msg && <div className={`alert alert-${msg.type} py-2`} role="alert" data-testid="tug-inventory-message">{msg.text}</div>}
        <div className="small fw-bold text-muted mb-2">Deliveries awaiting receipt</div>
        {pending.length === 0 ? (
          <p className="text-muted small" data-testid="no-deliveries">Nothing on its way to this tug.</p>
        ) : (
          pending.map((p) => (
            <div className="border rounded p-2 mb-2" key={p.id} data-testid="delivery-row" data-item={p.item}>
              <div className="small mb-2">
                <span className="fw-bold">{p.item}</span> · {amount(p.qty, p.unit)} from {p.from} · sent {when(p.sentAt)}
                {p.note ? <span className="text-muted"> · {p.note}</span> : null}
              </div>
              <div className="row g-2 align-items-end">
                <div className="col-4 col-md-2">
                  <label htmlFor={`rq-${p.id}`} className="form-label small fw-bold text-muted mb-1">Qty received</label>
                  <input id={`rq-${p.id}`} type="number" min="0" step="any" className="form-control form-control-sm" value={draft(p).qty} onChange={(e) => edit(p, { qty: e.target.value })} data-testid="delivery-qty" />
                </div>
                <div className="col-8 col-md-4">
                  <label htmlFor={`rs-${p.id}`} className="form-label small fw-bold text-muted mb-1">Stored in</label>
                  <input id={`rs-${p.id}`} list="stored-in-places" className="form-control form-control-sm" maxLength={120} placeholder="Engine room store, deck locker…" value={draft(p).storedIn} onChange={(e) => edit(p, { storedIn: e.target.value })} data-testid="delivery-stored-in" />
                </div>
                <div className="col-8 col-md-4">
                  <label htmlFor={`rn-${p.id}`} className="form-label small fw-bold text-muted mb-1">Note</label>
                  <input id={`rn-${p.id}`} className="form-control form-control-sm" maxLength={2000} value={draft(p).note} onChange={(e) => edit(p, { note: e.target.value })} data-testid="delivery-note" />
                </div>
                <div className="col-4 col-md-2">
                  <button type="button" className="btn btn-primary btn-sm w-100" onClick={() => confirm(p)} data-testid="delivery-confirm">Confirm</button>
                </div>
              </div>
            </div>
          ))
        )}
        <datalist id="stored-in-places">{suggest.storedIn.map((s) => <option key={s} value={s} />)}</datalist>

        <div className="small fw-bold text-muted mt-3 mb-2">Record an item used up</div>
        <form className="row g-2 align-items-end" onSubmit={recordUsed} noValidate>
          <div className="col-md-4">
            <label htmlFor="used-item" className="form-label small fw-bold text-muted mb-1">Item</label>
            <input id="used-item" list="used-items" className="form-control form-control-sm" maxLength={200} value={used.item} onChange={(e) => setUsed({ ...used, item: e.target.value })} data-testid="used-item" />
            <datalist id="used-items">{suggest.items.map((i) => <option key={i} value={i} />)}</datalist>
          </div>
          <div className="col-4 col-md-2">
            <label htmlFor="used-qty" className="form-label small fw-bold text-muted mb-1">Qty</label>
            <input id="used-qty" type="number" min="0" step="any" className="form-control form-control-sm" value={used.qty} onChange={(e) => setUsed({ ...used, qty: e.target.value })} data-testid="used-qty" />
          </div>
          <div className="col-4 col-md-2">
            <label htmlFor="used-unit" className="form-label small fw-bold text-muted mb-1">Unit</label>
            <input id="used-unit" className="form-control form-control-sm" maxLength={24} value={used.unit} onChange={(e) => setUsed({ ...used, unit: e.target.value })} data-testid="used-unit" />
          </div>
          <div className="col-md-3">
            <label htmlFor="used-note" className="form-label small fw-bold text-muted mb-1">Note</label>
            <input id="used-note" className="form-control form-control-sm" maxLength={2000} value={used.note} onChange={(e) => setUsed({ ...used, note: e.target.value })} data-testid="used-note" />
          </div>
          <div className="col-4 col-md-1">
            <button type="submit" className="btn btn-outline-primary btn-sm w-100" data-testid="used-save">Record</button>
          </div>
        </form>
      </div>
    </div>
  );
}
